import type { DocumentPurpose, DocumentView } from '@myrewards/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement, type ReactNode, type Ref } from 'react';
import {
  CheckboxButton,
  CheckboxField,
  FileTrigger,
  Input,
  Link,
  NumberField,
  TextField,
} from 'react-aria-components';
import {
  deleteDocument,
  documentHref,
  documentKeys,
  replaceDocument,
  uploadDocument,
  type UploadInput,
} from '../api/ops';
import {
  Button,
  ConfirmDialog,
  cx,
  formatDate,
  Icon,
  StatusPill,
  useDollarCents,
  useToast,
  type ButtonVariant,
} from '../ui';
import { problemWords, SCAN } from './labels';
import s from './Ops.module.css';

/** The file types the server accepts (PDF, Word, Excel, PNG, JPEG and email). */
export const ACCEPTED_FILES = ['.pdf', '.docx', '.xlsx', '.png', '.jpg', '.jpeg', '.msg', '.eml'];

/** "< Queue": back to where the person came from, above the page's first card. */
export function BackLink({ href, children }: { href: string; children: string }): ReactElement {
  return (
    <Link href={href} className={s.back}>
      <Icon name="arrowLeft" size={16} />
      {children}
    </Link>
  );
}

/** Initials in a circle, with the name beside it (who holds a claim, who's on the team). */
export function PersonChip({
  initials,
  name,
  detail,
  tone = 'sky',
}: {
  initials: string;
  name: string;
  detail?: string;
  tone?: 'sky' | 'cyan';
}): ReactElement {
  return (
    <span className={s.personChip}>
      <span className={cx(s.avatar, tone === 'cyan' && s.avatarCyan)} aria-hidden="true">
        {initials}
      </span>
      <span className={s.personText}>
        <span className={s.personName}>{name}</span>
        {detail !== undefined && <span className={s.personDetail}>{detail}</span>}
      </span>
    </span>
  );
}

/** An inline message that's read out when it appears. Problems are never shown by colour alone. */
export function Notice({
  tone,
  children,
}: {
  tone: 'problem' | 'info' | 'done' | 'waiting';
  children: ReactNode;
}): ReactElement {
  const icon = tone === 'problem' ? 'close' : tone === 'done' ? 'check' : 'info';
  return (
    <p
      className={cx(
        s.notice,
        tone === 'problem' && s.noticeProblem,
        tone === 'done' && s.noticeDone,
        tone === 'waiting' && s.noticeWaiting,
      )}
      role={tone === 'problem' ? 'alert' : 'status'}
    >
      <Icon name={icon} size={16} />
      <span>{children}</span>
    </p>
  );
}

const PURPOSE: Record<DocumentPurpose, string> = {
  write_up: 'Write-up',
  original_ask: 'Original ask',
  proof: 'Proof',
  tax_estimate: 'ADP QuickCalc',
  letter: 'Letter',
  earning_statement: 'Earning statement',
  other: 'Supporting file',
  asked_for: 'Asked for',
};

function sizeWords(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function kindWords(doc: DocumentView): string {
  const ext = doc.fileName.split('.').pop()?.toUpperCase();
  return ext !== undefined && ext.length <= 4 ? ext : 'File';
}

/** The current version of each file (an older version points at nothing newer). */
export function currentFiles(docs: readonly DocumentView[]): DocumentView[] {
  const replaced = new Set(docs.flatMap((d) => (d.replacesId === null ? [] : [d.replacesId])));
  return docs.filter((d) => !replaced.has(d.id));
}

/** Keeps the documents list and everything that reads proof in step after a file change. */
function useFilesChanged(requestId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: documentKeys.one(requestId) }),
      queryClient.invalidateQueries({ queryKey: ['check', requestId] }),
      queryClient.invalidateQueries({ queryKey: ['approvals'] }),
    ]);
  };
}

/** Attach a file: a real button that opens the file picker; the upload follows at once. */
export function AttachButton({
  requestId,
  purpose,
  lineId,
  staffOnly,
  label,
  accessibleLabel,
  accept = ACCEPTED_FILES,
  variant = 'ghost',
  onUploaded,
}: Omit<UploadInput, 'file'> & {
  requestId: string;
  label: string;
  /** The button's name when a table repeats it ("Attach earning statement for Jordan Ellis"). */
  accessibleLabel?: string;
  /** File types the picker offers (an earning statement is a PDF). */
  accept?: readonly string[];
  variant?: ButtonVariant;
  onUploaded?: (doc: DocumentView) => void;
}): ReactElement {
  const toast = useToast();
  const changed = useFilesChanged(requestId);
  const [error, setError] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: (file: File) =>
      uploadDocument(requestId, {
        file,
        purpose,
        ...(lineId !== undefined && { lineId }),
        ...(staffOnly !== undefined && { staffOnly }),
      }),
    onSuccess: async ({ document }) => {
      setError(null);
      await changed();
      toast.show({
        tone: 'done',
        title: `${document.fileName} attached`,
        body: 'It opens once the virus check clears it, usually within a minute.',
      });
      onUploaded?.(document);
    },
    onError: (e) => {
      setError(problemWords(e));
    },
  });
  return (
    <div className={s.attach}>
      <FileTrigger
        acceptedFileTypes={[...accept]}
        onSelect={(files) => {
          const file = files?.[0];
          if (file) upload.mutate(file);
        }}
      >
        <Button
          variant={variant}
          size="sm"
          // The boards' upload control (P1, SL3, SL5, R3): a dashed box with an upload arrow.
          className={variant === 'ghost' ? s.attachButton : undefined}
          icon={<Icon name="upload" size={16} />}
          isPending={upload.isPending}
          {...(accessibleLabel !== undefined && { 'aria-label': accessibleLabel })}
        >
          {label}
        </Button>
      </FileTrigger>
      {error !== null && <Notice tone="problem">{error}</Notice>}
    </div>
  );
}

/**
 * The award's files of the given purposes: name, who added it and when, the virus check in words,
 * and Download once it's cleared. With `canChange`, a new version can replace a file and a file
 * can be removed (kept in its history, with a reason).
 */
export function FileList({
  requestId,
  documents,
  canChange,
  empty,
}: {
  requestId: string;
  documents: readonly DocumentView[];
  canChange: boolean;
  empty: ReactNode;
}): ReactElement {
  const files = currentFiles(documents);
  if (files.length === 0) return <>{empty}</>;
  return (
    <ul className={s.files}>
      {files.map((doc) => (
        <FileRow key={doc.id} requestId={requestId} doc={doc} canChange={canChange} />
      ))}
    </ul>
  );
}

function FileRow({
  requestId,
  doc,
  canChange,
}: {
  requestId: string;
  doc: DocumentView;
  canChange: boolean;
}): ReactElement {
  const toast = useToast();
  const changed = useFilesChanged(requestId);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scan = SCAN[doc.scanStatus];
  const replace = useMutation({
    mutationFn: (file: File) => replaceDocument(requestId, doc.id, file),
    onSuccess: async ({ document }) => {
      setError(null);
      await changed();
      toast.show({ tone: 'done', title: `${document.fileName} replaces ${doc.fileName}` });
    },
    onError: (e) => {
      setError(problemWords(e));
    },
  });
  const remove = useMutation({
    mutationFn: (reason: string) => deleteDocument(requestId, doc.id, reason),
    onSuccess: async () => {
      setError(null);
      await changed();
      toast.show({
        tone: 'done',
        title: `${doc.fileName} removed`,
        body: 'It stays in the history.',
      });
    },
    onError: (e) => {
      setError(problemWords(e));
    },
  });
  const added = [
    kindWords(doc),
    sizeWords(doc.sizeBytes),
    `added${doc.uploadedBy ? ` by ${doc.uploadedBy.name}` : ''} ${formatDate(doc.uploadedAt)}`,
  ].join(' · ');
  return (
    <li className={s.file}>
      <span className={s.fileIcon} aria-hidden="true">
        <Icon name={doc.contentType.startsWith('image/') ? 'page' : 'file'} size={20} />
      </span>
      <span className={s.fileText}>
        {scan.opens ? (
          <a href={documentHref(requestId, doc.id)} className={s.fileName} download>
            {doc.fileName}
            <span className="visually-hidden">{' (download)'}</span>
          </a>
        ) : (
          <span className={s.fileName}>{doc.fileName}</span>
        )}
        <span className={s.fileMeta}>
          {added}
          {doc.staffOnly && (
            <>
              {' · '}
              <Icon name="lock" size={12} /> Staff only
            </>
          )}
        </span>
        {error !== null && <Notice tone="problem">{error}</Notice>}
      </span>
      <span className={s.fileActions}>
        <StatusPill tone="info" size="sm" label={PURPOSE[doc.purpose]} />
        <StatusPill tone={scan.tone} size="sm" label={scan.label} />
        {canChange && (
          <>
            <FileTrigger
              acceptedFileTypes={ACCEPTED_FILES}
              onSelect={(files) => {
                const file = files?.[0];
                if (file) replace.mutate(file);
              }}
            >
              <Button
                variant="ghost"
                size="sm"
                isPending={replace.isPending}
                aria-label={`Replace ${doc.fileName}`}
              >
                Replace
              </Button>
            </FileTrigger>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Remove ${doc.fileName}`}
              onPress={() => {
                setConfirming(true);
              }}
            >
              Remove
            </Button>
            <ConfirmDialog
              isOpen={confirming}
              onOpenChange={setConfirming}
              title={`Remove ${doc.fileName}?`}
              intro="It comes off the award but stays in its history, with your reason."
              summary={[{ label: 'File', value: doc.fileName }]}
              reasonLabel="Why are you removing it?"
              confirmLabel="Remove the file"
              onConfirm={async (reason) => {
                await remove.mutateAsync(reason).catch(() => undefined);
              }}
            />
          </>
        )}
      </span>
    </li>
  );
}

/**
 * A dollar amount inside a table row, named by its row ("Cash for Dana Park"): the column heading
 * is the visible label. Reads and writes integer cents; empty is null. APP-253 (change 9): it
 * formats as it's typed ("$1,000.00"), and hands the cents over when they're entered, as before.
 */
export function AmountInput({
  label,
  valueCents,
  onChange,
  wholeDollars = true,
  maxCents,
  isDisabled = false,
  isInvalid = false,
  describedBy,
  inputRef,
}: {
  label: string;
  valueCents: number | null;
  onChange: (cents: number | null) => void;
  wholeDollars?: boolean;
  maxCents?: number;
  isDisabled?: boolean;
  /** Marks the box wrong; `describedBy` names the message that says why. */
  isInvalid?: boolean;
  describedBy?: string;
  inputRef?: Ref<HTMLInputElement>;
}): ReactElement {
  const { field, inputRef: boxRef } = useDollarCents({
    valueCents,
    onChangeCents: onChange,
    minCents: 0,
    maxCents,
    wholeDollars,
    inputRef,
  });
  return (
    <TextField
      {...field}
      aria-label={label}
      {...(describedBy !== undefined && { 'aria-describedby': describedBy })}
      isInvalid={isInvalid}
      isDisabled={isDisabled}
      className={s.amount}
    >
      <Input ref={boxRef} className={s.amountInput} />
    </TextField>
  );
}

/** A whole number of options inside a table row, named by its row. Empty is null. */
export function CountInput({
  label,
  value,
  onChange,
  isDisabled = false,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  isDisabled?: boolean;
}): ReactElement {
  return (
    <NumberField
      aria-label={label}
      value={value === null ? Number.NaN : value}
      minValue={0}
      step={1}
      isDisabled={isDisabled}
      formatOptions={{ maximumFractionDigits: 0 }}
      onChange={(n) => {
        onChange(Number.isNaN(n) ? null : Math.round(n));
      }}
      className={s.amount}
    >
      <Input className={s.amountInput} />
    </NumberField>
  );
}

/** A real checkbox with a 44 px target and its label beside it; the tick is never colour alone. */
export function TickBox({
  isSelected,
  onChange,
  isDisabled = false,
  isIndeterminate = false,
  label,
  children,
}: {
  isSelected: boolean;
  onChange: (on: boolean) => void;
  isDisabled?: boolean;
  /** Some, not all, of what it stands for are ticked (a Select all, APP-105). */
  isIndeterminate?: boolean;
  /** The accessible name when the visible text alone doesn't say what it's for. */
  label?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <CheckboxField
      isSelected={isSelected}
      onChange={onChange}
      isDisabled={isDisabled}
      isIndeterminate={isIndeterminate}
      {...(label !== undefined && { 'aria-label': label })}
    >
      <CheckboxButton className={s.checkbox}>
        {({ isSelected: on, isIndeterminate: some }) => (
          <>
            <span className={cx(s.box, (on || some) && s.boxOn)} aria-hidden="true">
              {some ? (
                <Icon name="minus" size={14} strokeWidth={3} />
              ) : (
                on && <Icon name="check" size={14} strokeWidth={3} />
              )}
            </span>
            {children}
          </>
        )}
      </CheckboxButton>
    </CheckboxField>
  );
}
